/**
 * bankImport/csvProfiles — Header-Signatur-Erkennung für CSV-Bank-Exporte
 * (architecture-bank-reconciliation.md §5.2/§9.1).
 *
 * v2-Straffung (§5.2, CFO 5): KEINE spekulativen built-in-Profile ohne real
 * vorliegende Fixture. Da aktuell (O-5) keine echten anonymisierten
 * Bank-Exporte von Daniel vorliegen, ist `BUILTIN_CSV_PROFILES` bewusst LEER
 * — jeder CSV-Import läuft v1 über den generischen Mapping-Wizard (BR-3).
 * Sobald Daniel echte Exporte liefert, werden built-in-Profile hier als
 * `BuiltinBankCsvProfile`-Konstanten ergänzt (kein Code-Refactor nötig, s.
 * `findProfileByHeader` unten).
 *
 * @module bankImport/csvProfiles
 */

import type { BankCsvProfile, BuiltinBankCsvProfile } from '../../types/bankTypes';
import { fnv1a64 } from './dedup';
import type { CsvProfileShape } from './types';

/**
 * Built-in-Profile — v1 bewusst leer (s. Modul-Kommentar). User-Profile
 * (`bank_csv_profiles`, gespeichert über den Mapping-Wizard, BR-3) sind der
 * Normalpfad.
 */
export const BUILTIN_CSV_PROFILES: ReadonlyArray<BuiltinBankCsvProfile> = [];

/**
 * Header-Signatur einer CSV-Zeile: normalisiert (trim, lowercase,
 * Whitespace-Kollaps) und per `fnv1a64` gehasht — derselbe Algorithmus wie
 * die Transaktions-Dedup-ID (§6.4), damit kein zweiter Hash-Mechanismus im
 * Modul existiert.
 *
 * @param headerLine - Die rohe Header-Zeile (bereits per Delimiter in eine
 *   Zeile zusammengeführt, z.B. mit `join(';')`, oder der Roh-Text der ersten Zeile).
 */
export function computeHeaderSignature(headerLine: string): string {
  const normalized = headerLine.trim().toLowerCase().replace(/\s+/g, ' ');
  return fnv1a64(normalized);
}

/**
 * Sucht ein passendes CSV-Profil per Header-Signatur. User-Profile sind
 * FÜHREND (§9.1/§5.2) — ein User-Profil mit identischer Signatur gewinnt
 * immer vor einem built-in.
 *
 * @param headerLine - Die rohe Header-Zeile der hochgeladenen Datei.
 * @param userProfiles - Alle gespeicherten `bank_csv_profiles`-Docs des Users.
 * @returns Das gefundene Profil (User- oder built-in-Shape) oder `null` →
 *   Mapping-Wizard-Pfad (BR-3).
 */
export function findProfileByHeader(headerLine: string, userProfiles: BankCsvProfile[]): CsvProfileShape | null {
  const signature = computeHeaderSignature(headerLine);

  const userMatch = userProfiles.find((profile) => profile.headerSignature === signature);
  if (userMatch) return userMatch;

  const builtinMatch = BUILTIN_CSV_PROFILES.find((profile) => profile.headerSignature === signature);
  return builtinMatch ?? null;
}
